import type { PostDownloadHooks} from '@mimir/client-common/model/ongoingDownload';
import { type OngoingDownload, getId } from '@mimir/client-common/model/ongoingDownload';
import type { NleProjectBin } from '@mimir/primitive/nle/bin';
import type { ImportToNleParams } from '@mimir/primitive/nle/import';
import { TaskUrgency } from '@mimir/common/async/taskPriority';
import { PrioritySemaphore, makePriority } from '@mimir/common/async/prioritySemaphore';
import isEmpty from 'lodash/isEmpty';
import { pendingBatchImports, removeBatchImport } from '../globals/batchImports';
import { batchImportToPremierePromise } from './importToPremiere';
import type { BatchImportInfo } from './interfaces';
import { isPremiereProjectFile } from './misc';
import { openProjectFile } from './premiereProject';
import { tryImport } from './tryImport';
import { isInElectron, type AdobePanelContext, type ElectronContext } from '@mimir/common/context/applicationContext';
import { convertSecondsToTicks } from '@mimir/client-common/util/premiere';
import type { NotificationOps } from '@mimir/client-common/fileSync/notificationOps';
import type { Logger } from '@mimir/common/types/logger';
import { tryGetLocallyAvailableFile } from '@mimir/client-common/fileSync/downloadDestinationUtils';
import type { LocalStateOps } from '@mimir/client-common/fileSync/localStateOps';

const batchImportExecutionSemaphore = new PrioritySemaphore(100);


export async function executeBatchImportUsingSemaphore(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
    localStateOps: LocalStateOps,
  },
  config: {
    downloadPath: string,
    userDataSystemPath: string,
    shouldUsePremierePreferences: boolean,
  },
  batchImportId: string,
) {
  await batchImportExecutionSemaphore.limit(
    () => checkAndExecuteBatchImport(services, config, batchImportId),
    makePriority({ urgency: TaskUrgency.LongRunning }),
    null
  );
}


async function checkAndExecuteBatchImport(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
    localStateOps: LocalStateOps,
  },
  config: {
    downloadPath: string,
    userDataSystemPath: string,
    shouldUsePremierePreferences: boolean,
  },
  batchImportId: string,
) {
  const batchImport: BatchImportInfo = pendingBatchImports[batchImportId];
  if (!batchImport) {
    services.logger.debug(`batchImport not found: ${batchImportId}`);
    return;
  }

  const newSequenceName = batchImport.sequenceName;

  if (!batchImport.allFilesAdded) {
    services.logger.debug(`batchImport still being created: ${batchImportId}`);
    return;
  }

  const postDownloadHooksAndFilePaths : { hooks: PostDownloadHooks, filePath: string }[] = [];

  // first pass, just check if all available
  const importParamsIndexedByTargetBin: {
    [key: string]: {
      targetBin?: NleProjectBin;
      importParams: ImportToNleParams[];
    }
  } = {};
  const pendingImportsIndex: Record<string, OngoingDownload> = {};
  const projectFiles = [];

  for (const filePendingImport of batchImport.filesPendingImport) {
    // Check if source item already exists in the list of files to be imported
    // to avoid importing duplicates
    const odId = getId(filePendingImport);
    const subclip = filePendingImport.subclip;
    const isSourceAlreadyIncluded = filePendingImport.doesMasterClipExistInBin || !!pendingImportsIndex[odId];

    if (!subclip && (isSourceAlreadyIncluded || !filePendingImport.importAfterCompletion)) {
      continue;
    }
    pendingImportsIndex[odId] = filePendingImport;

    if (filePendingImport.cancelTokenSource.token.reason) continue;

    const pathToImportFrom = await tryGetLocallyAvailableFile(services, filePendingImport, filePendingImport.importableLocalPath);
    if (pathToImportFrom === undefined) {
      services.logger.debug(`still waiting for ${filePendingImport.itemId} for batchImport ${batchImportId}`);
      return;
    }

    if (!filePendingImport.importAfterCompletion) {
      await tryImport(services, config, filePendingImport, pathToImportFrom);
      continue;
    }

    if (pathToImportFrom && isPremiereProjectFile(pathToImportFrom)) {
      projectFiles.push(filePendingImport);
      continue;
    }

    const targetBin = filePendingImport.targetBin;
    const importParam: ImportToNleParams = {
      filePath: pathToImportFrom!,
      title: filePendingImport.title!,
      subclipTitle: subclip ? subclip.title : '',
      startTime: subclip ? subclip.startTime : undefined,
      endTime: subclip ? subclip.endTime : undefined,
      sourceItemId: filePendingImport.itemId!,
      proxyFilePath: filePendingImport.artefact === 'proxy' && pathToImportFrom || '',
      subclipId: subclip ? subclip.id : '',
      tapeName: filePendingImport.originalFileName,
      artifactReference:  filePendingImport.artifactReference,
      markers: filePendingImport.markers,
      sequenceOrder: filePendingImport.sequenceOrder,
      startTimeInTicks: filePendingImport.premiereStartTimeSeconds ? convertSecondsToTicks(filePendingImport.premiereStartTimeSeconds) : null,
      timeDisplayFormat: filePendingImport.premiereTimeDisplayFormat,
      mediaFrameRate: filePendingImport.mediaFrameRate,
      mogrtChildrenMap: filePendingImport.mogrtChildrenMap,
      timelineInsertion: filePendingImport.timelineInsertion,
    };
    postDownloadHooksAndFilePaths.push({ hooks: filePendingImport.postDownloadHooks, filePath: importParam.filePath });
    let key = 'root';
    if (targetBin) {
      if (!importParamsIndexedByTargetBin[targetBin.nodeId]) {
        importParamsIndexedByTargetBin[targetBin.nodeId] = {
          targetBin,
          importParams: [],
        };
      }
      key = targetBin.nodeId;
    } else if (!importParamsIndexedByTargetBin['root']) {
      importParamsIndexedByTargetBin['root'] = { importParams: [] };
    }

    if (subclip) {
      if (!importParamsIndexedByTargetBin[key]) {
        importParamsIndexedByTargetBin[key] = {
          targetBin,
          importParams: [],
        };
      }
      importParamsIndexedByTargetBin[key].importParams.push(importParam);

    } else if (!isSourceAlreadyIncluded) {
      importParamsIndexedByTargetBin[key].importParams.push(importParam);
    }
  }

  // This the completion check above is async it is possible for two checks to
  // reach this point. From here only the one that is successfully able to
  // remove the batch import is allowed to proceed.
  if (!removeBatchImport(batchImportId)) {
    return;
  }

  if (isEmpty(importParamsIndexedByTargetBin)) {
    batchImport.notifier?.remove();
  }

  if (!isInElectron(services.applicationContext)) {
    batchImport.notifier?.setDescription('Importing items...');
  }
  const totalBatches = Object.keys(importParamsIndexedByTargetBin).length;
  let processedBatchesCount = 0;

  const onBatchImportCompleted = (batchNo: number) => {
    if (batchNo === totalBatches) {
      if (!isInElectron(services.applicationContext) || services.applicationContext.dvrMode || !batchImport.notifier?.folderName()) {
        batchImport.notifier?.remove();
      } else {
        batchImport.notifier?.setDescription('Download complete.');
        batchImport.notifier?.setLoading(false);
        batchImport.notifier?.markCompleted();
      }
    }
  };
  const processBatchImport = async (node: string, index: typeof importParamsIndexedByTargetBin) => {
    processedBatchesCount += 1;
    const batchNo = processedBatchesCount;


    const { importParams, targetBin } = index[node];

    if (importParams.length > 0) {
      await batchImportToPremierePromise(services.applicationContext, importParams, config.shouldUsePremierePreferences, targetBin, newSequenceName, () => onBatchImportCompleted(batchNo));
    }
  };

  await Promise.all(postDownloadHooksAndFilePaths.map(({ hooks, filePath }) => Promise.all(hooks.map(hook => hook(filePath)))));

  for (const node in importParamsIndexedByTargetBin) {
    await processBatchImport(node, importParamsIndexedByTargetBin);
  }

  for (const file of projectFiles) {
    const pathToOpen = await tryGetLocallyAvailableFile(services, file, file.importableLocalPath);
    if (pathToOpen !== undefined) {
      await openProjectFile(services, config, {
        projectOriginalFileName: file.originalFileName || '',
        filePath: pathToOpen,
        title: file.title!,
        itemId: file.itemId!,
        suppressWarning: true,
        isProjectTemplate: false,
      });
    }
  }
}
